import type { WProductLookView, WProductView, WSkuPreview } from '@zola/svc-web-api-ts-client';

import { formatPrice } from '@/util/currencyFormatter';

export const findProductLookByKey = (
  key: string,
  productLooks: WProductLookView[] | undefined
): WProductLookView | undefined => {
  const filteredProductLooks = (productLooks || []).filter(
    (productLook) => productLook.key === key
  );
  return filteredProductLooks[0];
};

export const findSkuById = <T extends { id?: string }>(
  skus: T[],
  skuId: string | undefined
): T | undefined => {
  if (!skuId) {
    return undefined;
  }

  const filteredSkus = skus.filter((sku) => sku.id === skuId);
  return filteredSkus[0] ?? undefined;
};

export type RegistryItemAddedEventData = {
  sku: string | undefined;
  name: string | undefined;
  brand: string | undefined;
  quantity: number;
  price: string | number | null;
  location?: string;
  section?: string;
  collection_key?: string;
  collection_name?: string;
  recommendation_source?: string;
  endpoint_variant?: string;
};
export const getRegistryItemAddedEventData = ({
  productData,
  activeSku,
  quantity,
  location = 'PDP',
  section = 'MAIN_CTA',
  extraData = {},
}: {
  productData: WProductView | null;
  activeSku: WSkuPreview;
  quantity: number;
  location?: string;
  section?: string;
  extraData?: {
    section?: string;
    collection_key?: string;
    collection_name?: string;
    recommendation_source?: string;
    endpoint_variant?: string;
  };
}): RegistryItemAddedEventData => {
  const brand = productData?.brand;
  return {
    sku: activeSku.id,
    name: productData?.name,
    brand: brand?.name,
    quantity,
    price: formatPrice(activeSku.price_cents, true), // flag to format for segment
    location,
    section,
    ...extraData,
  };
};

type KitRegistryItemAddedEventData = {
  name: string | undefined;
  brand: string | undefined;
  quantity: number;
  location?: string;
  section?: string;
  collection_key?: string;
  collection_name?: string;
  sku?: string;
  price?: string | number | null;
};
export type KitRegistryItemAddedEventParams = {
  productData: WProductView | null;
  activeProductLook: WProductLookView | null;
  activeSku: WSkuPreview | null;
  quantity: number;
  location?: string;
  section?: string;

  extraData?: {
    collection_key?: string;
    collection_name?: string;
  };
};
export const getKitRegistryItemAddedEventData = ({
  productData,
  activeProductLook,
  activeSku,
  quantity,
  location,
  section,
  extraData = {},
}: KitRegistryItemAddedEventParams): KitRegistryItemAddedEventData => {
  const brand = productData?.brand;
  const eventData: KitRegistryItemAddedEventData = {
    name: productData?.name,
    brand: brand?.name,
    quantity,
    location,
    section,
    ...extraData,
  };
  if (activeSku) {
    eventData.sku = activeSku.id;
    eventData.price = formatPrice(activeSku.price_cents, true); // flag to format for segment
  } else if (activeProductLook && activeProductLook.price_range) {
    eventData.price = Object.values(activeProductLook.price_range)
      .map((item) => formatPrice(item, true))
      .join(' - ');
  }
  return eventData;
};

type KitProductAddedEventData = {
  product_id: string | undefined;
  sku: string | undefined;
  brand: string | undefined;
  name: string | undefined;
  variant: string | undefined;
  quantity: number | undefined;
  price: string | number | null;
  location?: string;
  section?: string;
  cart_id?: string;
  position: number;
};
export const getKitProductAddedEventData = ({
  productData,
  activeProductLook,
  activeSku,
  quantity,
  cartId,
  location,
  section,
  position,
}: {
  productData: WProductView | null;
  activeProductLook: WProductLookView | null;
  activeSku: WSkuPreview;
  quantity: number;
  location?: string;
  section?: string;
  cartId?: string;
  position: number;
}): KitProductAddedEventData => {
  const brand = productData?.brand;
  return {
    product_id: productData?.id,
    sku: activeSku.id,
    brand: brand && brand.name,
    name: productData?.name,
    variant: activeProductLook?.id,
    quantity,
    price: formatPrice(activeSku.price_cents, true), // flag to format for segment
    location,
    section,
    cart_id: cartId,
    position,
  };
};
